import {
  useQuery,
  DocumentNode,
  QueryHookOptions,
  ApolloError,
  OperationVariables,
  MutationHookOptions,
  MutationTuple,
  useMutation,
  useLazyQuery,
  LazyQueryHookOptions,
  QueryTuple,
} from "@apollo/client";
import client from "@command-center-app/graphql/apolloClient";
import { ServiceName } from "..";

// Define a generic type for GraphQL responses
type UseApolloReturn<TData> = {
  data?: TData;
  loading: boolean;
  error?: ApolloError;
  refetch?: () => void;
};

// Custom Apollo hook that accepts a service name and query
export const useApolloQuery = <
  TData,
  TVariables extends OperationVariables = OperationVariables
>(
  service: ServiceName,
  query: DocumentNode,
  options?: QueryHookOptions<TData, TVariables>
): UseApolloReturn<TData> => {
  const { data, loading, error, refetch } = useQuery<TData, TVariables>(query, {
    client, // Use shared Apollo Client
    context: { service }, // Pass service dynamically
    ...options,
  });

  return { data, loading, error, refetch };
};

export const useApolloLazyQuery = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  service: ServiceName,
  query: DocumentNode,
  options?: LazyQueryHookOptions<TData, TVariables>
): QueryTuple<TData, TVariables> => {
  return useLazyQuery<TData, TVariables>(query, {
    client, // Use shared Apollo Client
    context: { service }, // Inject service into context
    ...options,
  });
};

export const useApolloMutation = <
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  service: ServiceName,
  mutation: DocumentNode,
  options?: MutationHookOptions<TData, TVariables>
): MutationTuple<TData, TVariables> => {
  return useMutation<TData, TVariables>(mutation, {
    client, // Use shared Apollo Client
    context: { service }, // Pass service dynamically
    ...options,
  });
};
